import {
  collapseColumn,
  column,
  ContentHeader,
  Css,
  emptyCell,
  FilterDefs,
  GridCellContent,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  groupColumns,
  LoadingSkeleton,
  maybeTooltip,
  multiFilter,
  numericColumn,
  Palette,
  simpleGroupHeader,
  simpleHeader,
  Tag,
  Tokens,
  Tooltip,
  useGridTableApi,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatToPrice, priceCell } from "src/components";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  CheckoutConfigOptionGroupStep,
  CostOptionGroupOfferingStatus,
  CostOptionGroupSummariesFilter,
} from "src/generated/graphqlTypes";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { count, formatList, queryResult } from "src/utils";
import { ReloadCostsBanner, ReloadCostsBannerType } from "./components/ReloadCostsBanner";
import {
  OptionsTab_ChildGroupFragment,
  OptionsTab_CostOptionGroupRollupFragment,
  OptionsTab_RollupStatsFragment,
  OptionsTabQuery,
  useOptionsTabFiltersQuery,
  useOptionsTabQuery,
} from "./OptionsTab.generated";
import { ComparisonFilter, deltaPercent, deltaPercentageCell, deltaPriceCell, selectedOfferingIds } from "./utils";

type OptionsTabProps = { productOfferings: NamedFragment[] };
type OptionsFilter = ComparisonFilter & { checkoutStep?: CheckoutConfigOptionGroupStep[]; optionType?: string[] };
/** The Product Offering filter option that shows the All Product Offerings columns. */
const summaryOption = { id: "summary", name: "Product Offerings Summary" };

export function OptionsTab(props: OptionsTabProps) {
  const { productOfferings } = props;
  const { id: projectId } = useProjectContext();
  const productOfferingIds = useMemo(() => productOfferings.map((po) => po.id), [productOfferings]);
  const filtersQuery = useOptionsTabFiltersQuery();
  const filterDefs = useMemo<FilterDefs<OptionsFilter>>(
    () => ({
      checkoutStep: multiFilter({
        label: "Checkout Section",
        options: filtersQuery.data?.enumDetails.checkoutConfigOptionGroupStep ?? [],
        getOptionValue: (s) => s.code,
        getOptionLabel: (s) => s.name,
      }),
      // The kinds of option group the summaries cover
      optionType: multiFilter({
        label: "Option Type",
        options:
          filtersQuery.data?.globalOptionTypes.filter(
            (t) => t.isAddOnV2 || t.isStructuralV2 || t.isPackageOptionV2 || t.isProductOptionV2,
          ) ?? [],
        getOptionValue: (t) => t.id,
        getOptionLabel: (t) => t.name,
      }),
      productOffering: multiFilter({
        label: "Product Offering",
        options: [...productOfferings.sortBy((po) => po.name), summaryOption],
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
        autoSort: false,
      }),
    }),
    [productOfferings, filtersQuery.data],
  );
  const layoutState = useGridTableLayoutState<OptionsFilter>({
    persistedFilter: { filterDefs, storageKey: `${projectId}_costAnalysisOptionsFilter` },
    search: "server",
  });
  const { productOffering } = layoutState.filter;
  // Nothing selected shows the summary and every offering, otherwise only what's picked
  const showSummary = !productOffering?.nonEmpty || productOffering.includes(summaryOption.id);
  const filter = useMemo(
    () => createFilter(layoutState.filter, layoutState.searchString, productOfferingIds),
    [layoutState.filter, layoutState.searchString, productOfferingIds],
  );
  const selectedOfferings = useMemo(
    () => productOfferings.filter((po) => filter.productOfferings.includes(po.id)),
    [productOfferings, filter.productOfferings],
  );
  const api = useGridTableApi<Row>();
  // One row per option group with its checkout locations under it, across the offerings, so they're all loaded and
  // sorted here
  const query = useOptionsTabQuery({ variables: { filter }, skip: productOfferingIds.isEmpty });
  // Each location's child summaries, loaded the first time it's expanded
  const [loadedChildren, setLoadedChildren] = useState<LoadedChildren>({});
  const onChildrenLoaded = useCallback(
    (location: LocationRow, children: LocationRow[]) =>
      setLoadedChildren((loaded) => ({ ...loaded, [location.summaryIdsWithChildren.join(",")]: children })),
    [],
  );
  const columns = useMemo(
    () => createColumns(selectedOfferings, showSummary, onChildrenLoaded),
    [selectedOfferings, showSummary, onChildrenLoaded],
  );
  const createRows = useCallback(
    (data: OptionsTabQuery | undefined): GridDataRow<Row>[] => [
      simpleGroupHeader,
      simpleHeader,
      ...groupByGlobalOptionGroup(data?.costOptionGroupSummaries ?? []).map((group) => ({
        kind: "group" as const,
        id: group.globalOptionGroup.id,
        data: group,
        initCollapsed: true,
        // A group at one location is a single row, with its child summaries under it. If there are multiple, each
        // location is a row under it, with its child summaries under that.
        children:
          group.locations.length > 1
            ? group.locations.map((location) => ({
                kind: "location" as const,
                id: location.id,
                data: location,
                initCollapsed: true,
                children: toChildRows(location, loadedChildren),
              }))
            : toChildRows(group.locations[0], loadedChildren),
      })),
    ],
    [loadedChildren],
  );
  return (
    <>
      <ReloadCostsBanner
        type={ReloadCostsBannerType.Options}
        productOfferingIds={filter.productOfferings}
        // Reloads the rows, and the children of the expanded ones as they show again
        onReload={() => {
          setLoadedChildren({});
          return query.refetch();
        }}
      />
      <ContentHeader
        title="Options"
        description="Review option costs and how they vary across Product Offerings. Costs recalculate automatically whenever a cost-changing edit is saved."
      />
      <GridTableLayout
        layoutState={layoutState}
        emptyFallback="There aren't any option groups to display."
        // Columns with the same name share one Edit Columns switch
        shareColumnVisibility
        tableProps={{
          api,
          query,
          createRows,
          columns,
          sorting: { on: "client", initial: ["name", "ASC"] },
          as: "virtual",
          // Stripes every other visible group, so leaving out the summary doesn't leave two plain groups side by side
          style: { columnStriping: Palette.Gray100 },
        }}
      />
    </>
  );
}

type Offering = {
  status: CostOptionGroupOfferingStatus;
  costs: OptionsTab_RollupStatsFragment | undefined;
  prices: OptionsTab_RollupStatsFragment | undefined;
};
type Offerings = Partial<Record<string, Offering>>;
type LocationRow = Omit<OptionsTab_CostOptionGroupRollupFragment, "offerings"> & {
  id: string;
  offerings: Offerings;
  summaryIdsWithChildren: string[];
  childGroups: OptionsTab_ChildGroupFragment[];
};
type ChildRow = LocationRow & { parent: LocationRow };
type LoadedChildren = Partial<Record<string, LocationRow[]>>;
type GroupRow = Pick<OptionsTab_CostOptionGroupRollupFragment, "globalOptionGroup" | "optionCode"> & {
  locations: LocationRow[];
  costs: OptionsTab_RollupStatsFragment;
  prices: OptionsTab_RollupStatsFragment;
  offerings: Offerings;
};
type Row =
  | { kind: "header" }
  | { kind: "groupHeader" }
  | { kind: "group"; data: GroupRow }
  | { kind: "location"; data: LocationRow }
  | { kind: "child"; data: ChildRow }
  | { kind: "loading"; data: LocationRow };

/** One row per global option group, adding up its locations, which become the rows under it. */
function groupByGlobalOptionGroup(rollups: OptionsTab_CostOptionGroupRollupFragment[]): GroupRow[] {
  return Object.values(rollups.groupBy((rollup) => rollup.globalOptionGroup.id)).map((groupRollups) => {
    const { globalOptionGroup, optionCode } = groupRollups[0];
    const locations = groupRollups.map(toLocationRow);
    // Each offering's total over the locations it has the group at, else why it has none at any of them
    const offerings = groupRollups[0].offerings.keyBy(
      ({ productOffering }) => productOffering.id,
      ({ productOffering }): Offering => {
        const cells = locations.map((location) => location.offerings[productOffering.id]).compact();
        const costs = cells.map((cell) => cell.costs).compact();
        if (costs.isEmpty) {
          const notOffered = cells.some((cell) => cell.status === CostOptionGroupOfferingStatus.NotOffered);
          return {
            status: notOffered ? CostOptionGroupOfferingStatus.NotOffered : CostOptionGroupOfferingStatus.NotApplicable,
            costs: undefined,
            prices: undefined,
          };
        }
        return {
          status: CostOptionGroupOfferingStatus.Costed,
          costs: sumStats(costs),
          prices: sumStats(cells.map((cell) => cell.prices).compact()),
        };
      },
    );
    const totals = Object.values(offerings);
    return {
      globalOptionGroup,
      optionCode,
      locations,
      costs: calcSpread(totals.map((total) => total.costs).compact()),
      prices: calcSpread(totals.map((total) => total.prices).compact()),
      offerings,
    };
  });
}

/** The location's row, with each offering's summary as stats like the group row's. */
function toLocationRow(rollup: OptionsTab_CostOptionGroupRollupFragment): LocationRow {
  return {
    ...rollup,
    id: `${rollup.globalOptionGroup.id}_${rollup.locationName ?? "none"}`,
    summaryIdsWithChildren: rollup.offerings
      .map(({ summary }) => summary)
      .compact()
      .filter((summary) => summary.children.nonEmpty)
      .map((summary) => summary.id),
    childGroups: rollup.offerings
      .flatMap(({ summary }) => summary?.children ?? [])
      .map((child) => child.readyPlanOptionGroup.group)
      .filter((group) => group.id !== rollup.globalOptionGroup.id)
      .uniqueByKey("id"),
    offerings: rollup.offerings.keyBy(
      ({ productOffering }) => productOffering.id,
      ({ status, summary }) => ({
        status,
        costs: summary
          ? {
              minInCents: summary.minCostInCents,
              maxInCents: summary.maxCostInCents,
              averageInCents: summary.averageCostInCents,
              deltaInCents: summary.deltaCostInCents,
              deltaPercentage: summary.deltaCostPercentage,
            }
          : undefined,
        prices: summary
          ? {
              minInCents: summary.minPriceInCents,
              maxInCents: summary.maxPriceInCents,
              averageInCents: summary.averagePriceInCents,
              deltaInCents: summary.deltaPriceInCents,
              deltaPercentage: summary.deltaPricePercentage,
            }
          : undefined,
      }),
    ),
  };
}

/** A location's child summaries as the rows under it, or a placeholder that loads them once it shows. */
function toChildRows(location: LocationRow, loadedChildren: LoadedChildren): GridDataRow<Row>[] | undefined {
  if (location.summaryIdsWithChildren.isEmpty) return undefined;
  const children = loadedChildren[location.summaryIdsWithChildren.join(",")];
  if (!children) return [{ kind: "loading", id: `${location.id}_loading`, data: location }];
  return children.map((child): GridDataRow<Row> => {
    // The parent's own group, i.e. the lines no other group splits
    const isParentGroup = child.globalOptionGroup.id === location.globalOptionGroup.id;
    return {
      kind: "child",
      id: `${location.id}_${child.id}`,
      data: { ...child, parent: location },
      fixedSort: isParentGroup ? "first" : undefined,
    };
  });
}

/** What a group's row opens into, e.g. "2 locations × 1 Elevation × 3 Add-Ons", or nothing when it doesn't. */
function getChildrenDescription(group: GroupRow): string | undefined {
  const { locations } = group;
  // How many groups of each type its cost varies by at any of its locations, in the types' order
  const groupCounts = Object.values(
    locations
      .flatMap((location) => location.childGroups)
      .uniqueByKey("id")
      .groupBy((group) => group.type.name),
  )
    .sortBy(([{ type }]) => [type.order, type.name])
    .map((groups) => count(groups, groups[0].type.name));
  const counts = [locations.length > 1 ? count(locations, "location") : undefined, ...groupCounts].compact();
  return counts.nonEmpty ? counts.join(" × ") : undefined;
}

/** The stats added up: the totals of their cheapest, priciest and average configurations. */
function sumStats(stats: OptionsTab_RollupStatsFragment[]): OptionsTab_RollupStatsFragment {
  return toStats({
    minInCents: stats.sum((s) => s.minInCents),
    maxInCents: stats.sum((s) => s.maxInCents),
    averageInCents: stats.sum((s) => s.averageInCents),
  });
}

/** The spread between the stats, with the mean of their averages. */
function calcSpread(stats: OptionsTab_RollupStatsFragment[]): OptionsTab_RollupStatsFragment {
  return toStats({
    minInCents: stats.min((s) => s.minInCents),
    maxInCents: stats.max((s) => s.maxInCents),
    averageInCents: Math.round(stats.sum((s) => s.averageInCents) / stats.length),
  });
}

/** The amounts with their spread in dollars and as a percent of the min. */
function toStats(
  amounts: Pick<OptionsTab_RollupStatsFragment, "minInCents" | "maxInCents" | "averageInCents">,
): OptionsTab_RollupStatsFragment {
  const { minInCents, maxInCents } = amounts;
  return { ...amounts, deltaInCents: maxInCents - minInCents, deltaPercentage: deltaPercent(maxInCents, minInCents) };
}

/** Maps the table's filter to the query's filter, where an unselected filter means every value. */
function createFilter(
  filter: OptionsFilter,
  searchString: string | undefined,
  activeProductOfferingIds: string[],
): CostOptionGroupSummariesFilter {
  const { productOffering, checkoutStep, optionType } = filter;
  return {
    productOfferings: selectedOfferingIds(
      productOffering?.filter((id) => id !== summaryOption.id),
      activeProductOfferingIds,
    ),
    ...(checkoutStep?.nonEmpty && { checkoutStep }),
    ...(optionType?.nonEmpty && { optionType }),
    ...(searchString && { search: searchString }),
  };
}

function createColumns(
  offerings: NamedFragment[],
  showSummary: boolean,
  onChildrenLoaded: ChildRowsLoaderProps["onLoaded"],
): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({ sticky: "left" }),
    column<Row>({
      id: "name",
      name: "Name",
      header: "Name",
      groupHeader: emptyCell,
      canHide: false,
      group: (group) => {
        const subtitle = getChildrenDescription(group);
        return {
          content: () => (
            <div>
              {group.globalOptionGroup.name}
              {subtitle && <div css={Css.xs2.black.$}>{subtitle}</div>}
            </div>
          ),
          value: group.globalOptionGroup.name,
        };
      },
      location: ({ locationName }) => locationName ?? "",
      child: ({ globalOptionGroup }) => globalOptionGroup.name,
      loading: (location) => ({
        content: () => <ChildRowsLoader location={location} offerings={offerings} onLoaded={onChildrenLoaded} />,
        value: "",
      }),
      mw: "240px",
      sticky: "left",
    }),
    column<Row>({
      id: "type",
      name: "Type",
      header: "Type",
      groupHeader: emptyCell,
      canHide: true,
      group: ({ globalOptionGroup }) => typeCell(globalOptionGroup.type),
      location: emptyCell,
      child: emptyCell,
      loading: emptyCell,
      mw: "140px",
    }),
    column<Row>({
      id: "location",
      name: "Location",
      header: "Location",
      groupHeader: emptyCell,
      canHide: false,
      group: ({ locations }) =>
        locations.length > 1 ? `${locations.length} locations` : (locations[0]?.locationName ?? ""),
      location: ({ locationName }) => locationName ?? "",
      child: ({ locationName }) => locationName ?? "",
      loading: emptyCell,
      mw: "140px",
    }),
    column<Row>({
      id: "code",
      name: "Code",
      header: "Code",
      groupHeader: emptyCell,
      canHide: true,
      group: ({ optionCode }) => optionCode ?? "",
      location: emptyCell,
      child: ({ optionCode }) => optionCode ?? "",
      loading: emptyCell,
      mw: "120px",
    }),
    ...(showSummary ? summaryColumns(offerings) : []),
    ...offerings.flatMap((po) => groupColumns<Row>({ id: po.id, header: po.name, columns: offeringColumns(po) })),
  ];
}

/** The columns adding up the offerings, which the user can leave out with the Product Offering filter. */
function summaryColumns(offerings: NamedFragment[]): GridColumn<Row>[] {
  return groupColumns<Row>({
    id: "allProductOfferings",
    header: "All Product Offerings",
    columns: [
      numericColumn<Row>({
        id: "costRange",
        name: "Cost",
        header: "Cost Range",
        groupHeader: emptyCell,
        canHide: true,
        mw: "200px",
        group: (group) => summaryRangeCell(group, offerings),
        location: (location) => summaryRangeCell(location, offerings),
        child: (child) => summaryRangeCell(child, offerings),
        loading: emptyCell,
      }),
      numericColumn<Row>({
        id: "averageCostInCents",
        name: "Cost",
        header: "Avg Cost",
        groupHeader: emptyCell,
        canHide: true,
        mw: "120px",
        group: ({ costs }) => priceCell({ valueInCents: costs.averageInCents }),
        location: ({ costs }) => priceCell({ valueInCents: costs.averageInCents }),
        child: ({ costs }) => priceCell({ valueInCents: costs.averageInCents }),
        loading: emptyCell,
      }),
      numericColumn<Row>({
        id: "costDeltaInCents",
        name: "Δ Cost $",
        header: "Δ Cost $",
        groupHeader: emptyCell,
        canHide: true,
        initHidden: true,
        mw: "120px",
        group: ({ costs }) => deltaPriceCell(costs.deltaInCents),
        location: ({ costs }) => deltaPriceCell(costs.deltaInCents),
        child: ({ costs }) => deltaPriceCell(costs.deltaInCents),
        loading: emptyCell,
      }),
      numericColumn<Row>({
        id: "costDeltaPercentage",
        name: "Δ Cost %",
        header: "Δ Cost %",
        groupHeader: emptyCell,
        canHide: true,
        initHidden: true,
        mw: "120px",
        group: ({ costs }) => deltaPercentageCell(costs.deltaPercentage),
        location: ({ costs }) => deltaPercentageCell(costs.deltaPercentage),
        child: ({ costs }) => deltaPercentageCell(costs.deltaPercentage),
        loading: emptyCell,
      }),
      numericColumn<Row>({
        id: "priceRange",
        name: "Price",
        header: "Price Range",
        groupHeader: emptyCell,
        canHide: true,
        initHidden: true,
        mw: "200px",
        group: ({ prices }) => rangeCell(prices),
        location: ({ prices }) => rangeCell(prices),
        child: ({ prices }) => rangeCell(prices),
        loading: emptyCell,
      }),
      numericColumn<Row>({
        id: "averagePriceInCents",
        name: "Price",
        header: "Avg Price",
        groupHeader: emptyCell,
        canHide: true,
        initHidden: true,
        mw: "120px",
        group: ({ prices }) => priceCell({ valueInCents: prices.averageInCents }),
        location: ({ prices }) => priceCell({ valueInCents: prices.averageInCents }),
        child: ({ prices }) => priceCell({ valueInCents: prices.averageInCents }),
        loading: emptyCell,
      }),
      numericColumn<Row>({
        id: "priceDeltaInCents",
        name: "Δ Price $",
        header: "Δ Price $",
        groupHeader: emptyCell,
        canHide: true,
        initHidden: true,
        mw: "120px",
        group: ({ prices }) => deltaPriceCell(prices.deltaInCents),
        location: ({ prices }) => deltaPriceCell(prices.deltaInCents),
        child: ({ prices }) => deltaPriceCell(prices.deltaInCents),
        loading: emptyCell,
      }),
      numericColumn<Row>({
        id: "priceDeltaPercentage",
        name: "Δ Price %",
        header: "Δ Price %",
        groupHeader: emptyCell,
        canHide: true,
        initHidden: true,
        mw: "120px",
        group: ({ prices }) => deltaPercentageCell(prices.deltaPercentage),
        location: ({ prices }) => deltaPercentageCell(prices.deltaPercentage),
        child: ({ prices }) => deltaPercentageCell(prices.deltaPercentage),
        loading: emptyCell,
      }),
    ],
  });
}

function offeringColumns(po: NamedFragment): GridColumn<Row>[] {
  return [
    numericColumn<Row>({
      id: po.id,
      name: "Cost",
      header: "Cost",
      groupHeader: emptyCell,
      mw: "200px",
      group: (group) => costCell(group.offerings[po.id], po, group.globalOptionGroup.name, group.locations),
      location: (location) => costCell(location.offerings[po.id], po, location.globalOptionGroup.name, [location]),
      // Blank when the offering's parent isn't split by the child group, or the parent's N/A or tag when it has no cost
      child: ({ offerings, parent }) =>
        parent.offerings[po.id]?.costs
          ? rangeCell(offerings[po.id]?.costs)
          : costCell(parent.offerings[po.id], po, parent.globalOptionGroup.name, [parent]),
      loading: emptyCell,
    }),
    numericColumn<Row>({
      id: `${po.id}_deltaCostInCents`,
      name: "Δ Cost $",
      header: "Δ Cost $",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      group: ({ offerings }) => deltaPriceCell(offerings[po.id]?.costs?.deltaInCents),
      location: ({ offerings }) => deltaPriceCell(offerings[po.id]?.costs?.deltaInCents),
      child: ({ offerings }) => deltaPriceCell(offerings[po.id]?.costs?.deltaInCents),
      loading: emptyCell,
    }),
    numericColumn<Row>({
      id: `${po.id}_deltaCostPercentage`,
      name: "Δ Cost %",
      header: "Δ Cost %",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      group: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.costs?.deltaPercentage),
      location: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.costs?.deltaPercentage),
      child: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.costs?.deltaPercentage),
      loading: emptyCell,
    }),
    numericColumn<Row>({
      id: `${po.id}_price`,
      name: "Price",
      header: "Price",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "200px",
      group: ({ offerings }) => rangeCell(offerings[po.id]?.prices),
      location: ({ offerings }) => rangeCell(offerings[po.id]?.prices),
      child: ({ offerings }) => rangeCell(offerings[po.id]?.prices),
      loading: emptyCell,
    }),
    numericColumn<Row>({
      id: `${po.id}_deltaPriceInCents`,
      name: "Δ Price $",
      header: "Δ Price $",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      group: ({ offerings }) => deltaPriceCell(offerings[po.id]?.prices?.deltaInCents),
      location: ({ offerings }) => deltaPriceCell(offerings[po.id]?.prices?.deltaInCents),
      child: ({ offerings }) => deltaPriceCell(offerings[po.id]?.prices?.deltaInCents),
      loading: emptyCell,
    }),
    numericColumn<Row>({
      id: `${po.id}_deltaPricePercentage`,
      name: "Δ Price %",
      header: "Δ Price %",
      groupHeader: emptyCell,
      canHide: true,
      initHidden: true,
      mw: "120px",
      group: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.prices?.deltaPercentage),
      location: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.prices?.deltaPercentage),
      child: ({ offerings }) => deltaPercentageCell(offerings[po.id]?.prices?.deltaPercentage),
      loading: emptyCell,
    }),
  ];
}

/**
 * The offering's cost range. N/A when it has none of the row's locations, or not offered when it has them but not the
 * group, each with a sentence on hover.
 */
function costCell(
  offering: Offering | undefined,
  po: NamedFragment,
  group: string,
  locations: Pick<OptionsTab_CostOptionGroupRollupFragment, "locationName">[],
): GridCellContent {
  if (!offering) return emptyCell;
  if (offering.costs) return rangeCell(offering.costs);
  // "location Great Room" for one location, or "any of the 2 locations" for a group at several
  const locationName = locations[0]?.locationName;
  const location =
    locations.length > 1 ? `any of the ${locations.length} locations` : locationName && `location ${locationName}`;
  const notApplicable = offering.status === CostOptionGroupOfferingStatus.NotApplicable;
  const reason = notApplicable
    ? `${po.name} does not include ${location}.`
    : `${po.name} does not offer ${group}${location ? ` in ${location}` : ""}.`;
  const text = notApplicable ? "N/A" : "Not offered";
  return {
    content: () => (
      <Tooltip title={reason}>
        <span css={Css.color(Tokens.OnSurfaceMuted).$}>{text}</span>
      </Tooltip>
    ),
    value: text,
  };
}

/** "$1.00 - $2.00", or one amount when they match, sorting by the min. */
function rangeCell(stats: OptionsTab_RollupStatsFragment | undefined): GridCellContent {
  if (!stats) return emptyCell;
  return { content: formatRange(stats), value: stats.minInCents };
}

/** The range of offerings with costs for this row, with the full count shown on hover. */
function summaryRangeCell(row: Pick<GroupRow, "costs" | "offerings">, offerings: NamedFragment[]): GridCellContent {
  // N/A offerings don't have the row's location, so they can't have costs for it
  const applicable = offerings.filter(
    (po) => row.offerings[po.id]?.status !== CostOptionGroupOfferingStatus.NotApplicable,
  );
  const covered = applicable.filter((po) => row.offerings[po.id]?.costs);
  const coverage =
    covered.length === applicable.length
      ? `All ${covered.length}/${applicable.length}`
      : `${covered.length} of ${applicable.length}`;
  return {
    content: () => (
      <div css={Css.df.fdc.aife.gapPx(4).$}>
        {formatRange(row.costs)}
        <Tooltip title={`Includes costs from ${formatList(covered.map((po) => po.name))}.`}>
          <span css={Css.xs2.$}>{coverage}</span>
        </Tooltip>
      </div>
    ),
    value: row.costs.minInCents,
  };
}

function formatRange({ minInCents, maxInCents }: OptionsTab_RollupStatsFragment): string {
  const min = formatToPrice({ valueInCents: minInCents });
  return minInCents === maxInCents ? min : `${min} - ${formatToPrice({ valueInCents: maxInCents })}`;
}

/** The group's type, abbreviated for personalization groups with the full type shown on hover. */
function typeCell(type: OptionsTab_CostOptionGroupRollupFragment["globalOptionGroup"]["type"]): GridCellContent {
  const label = type.isPackageOptionV2 ? "Package" : type.isProductOptionV2 ? "Product" : type.name;
  return {
    content: () =>
      maybeTooltip({
        title: label !== type.name ? type.name : undefined,
        children: (
          <div>
            <Tag type="neutral" text={label} />
          </div>
        ),
      }),
    value: type.name,
  };
}

type ChildRowsLoaderProps = {
  location: LocationRow;
  offerings: NamedFragment[];
  onLoaded: (location: LocationRow, children: LocationRow[]) => void;
};

/** The placeholder under a location, which loads its child summaries once it shows, i.e. when the location's expanded. */
function ChildRowsLoader({ location, offerings, onLoaded }: ChildRowsLoaderProps) {
  const query = useOptionsTabQuery({
    variables: { filter: { productOfferings: offerings.map((o) => o.id), parent: location.summaryIdsWithChildren } },
    fetchPolicy: "network-only",
  });
  const { data } = query;
  // The rows are built above the table, so the children go up to it and replace this row
  useEffect(() => {
    if (data) onLoaded(location, data.costOptionGroupSummaries.map(toLocationRow));
  }, [data, location, onLoaded]);
  const skeleton = () => <LoadingSkeleton size="sm" />;
  return queryResult(query, {
    loading: skeleton,
    data: skeleton,
    error: () => (
      <span css={Css.red600.$}>There was an error loading the rows. Collapse and expand the row to try again.</span>
    ),
  });
}
